import * as React from 'react';
import {connect} from 'react-redux';

import type {ReportMap, ReportFacet, ReportRowData, ReportCellData} from 'nutshell-core/types';

// @ts-expect-error
import {LoadingIcon} from 'shells/icon/svg/loading-icon';

import type {NutshellState} from '../../../store';

import * as ReportSelectors from '../reports-selectors';

import styles from './report-details-table.module.css';

type OwnProps = {
    title: string;
    facet: ReportFacet;
};

type StateProps = {
    detailRows: Array<ReportRowData> | null | undefined;
    totalsRow: ReportRowData | null | undefined;
    reportMaps: Array<ReportMap> | null | undefined;
};
type TableProps = OwnProps & StateProps;

function mapStateToProps(state: NutshellState): StateProps {
    return {
        detailRows: ReportSelectors.getCurrentReportTableRows(state),
        totalsRow: ReportSelectors.getCurrentReportTableTotals(state),
        reportMaps: ReportSelectors.getReportMapList(state),
    };
}

export class ReportDetailsTableComponent extends React.PureComponent<TableProps> {
    render() {
        const {detailRows, totalsRow, reportMaps, title, facet} = this.props;

        if (!detailRows || !totalsRow || !reportMaps) {
            return (
                <div className={styles['loading-container']}>
                    <LoadingIcon />
                </div>
            );
        }

        return (
            <div className={styles.container}>
                <table className={styles.table}>
                    <thead className={styles['table-header']}>
                        <tr>
                            <th>{title}</th>
                            {reportMaps.map((report) => {
                                return (
                                    <th key={report.id}>
                                        {getReportTableHeaderName(report, facet)}
                                    </th>
                                );
                            })}
                            <th className={styles.empty} />
                        </tr>
                    </thead>
                    <TableSection row={totalsRow} facet={facet} isTotal={true} />
                    {detailRows.map((row) => {
                        return <TableSection key={row.title} row={row} facet={facet} />;
                    })}
                </table>
            </div>
        );
    }
}

// @ts-expect-error
const connector = connect<TableProps, OwnProps, _, _, _, _>(mapStateToProps);
export const ReportDetailsTable = connector(ReportDetailsTableComponent);

const formatDataForCell = (cell: ReportCellData, facet: ReportFacet): string => {
    const formattedValue = cell[facet];

    if (!formattedValue) {
        return '';
    }

    return `${formattedValue.prefix}${formattedValue.formatted}${formattedValue.suffix}`;
};

/*
 * One-off to get the proper label for the report's column in the table. Normally, this is
 * just the name of the report, i.e. 'Sales', but since we name the report 'Leads', we need
 * to manually change that specific report name to either 'Count' or 'Value' based on the
 * facet applied.
 */
const getReportTableHeaderName = (report: ReportMap, facet: ReportFacet): string => {
    if (report.name !== 'Leads') {
        return report.name;
    }

    return facet === 'total' ? 'Value' : 'Count';
};

type TableSectionProps = {
    row: ReportRowData;
    facet: ReportFacet;
    isTotal?: boolean;
};

const TableSection = ({row, facet, isTotal}: TableSectionProps) => {
    if (!row.segments || !row.segments.length) {
        return (
            <tbody
                key={row.title}
                className={
                    isTotal
                        ? styles['table-section--no-segments--totals']
                        : styles['table-section--no-segments']
                }
            >
                <tr className={styles['table-section-row']}>
                    <td>{row.title}</td>
                    {row.totalsByReport.map((report) => {
                        return <td key={report.id}>{formatDataForCell(report, facet)}</td>;
                    })}
                    <td className={styles['quota-cell-hack']} />
                    <td className={styles.empty} />
                </tr>
            </tbody>
        );
    }

    return (
        <tbody className={isTotal ? styles['table-section--totals'] : styles['table-section']}>
            <tr className={styles['table-section-header']}>
                <td>{row.title}</td>
                {row.totalsByReport.map((report) => {
                    return <td key={report.id}>{formatDataForCell(report, facet)}</td>;
                })}
                <td className={styles.empty} />
            </tr>
            {row.segments
                ? row.segments.map((segment) => {
                      return (
                          <tr key={segment.title} className={styles['table-section-row']}>
                              <td>{segment.title}</td>
                              {segment.totalsByReport.map((report) => {
                                  return (
                                      <td key={report.id}>{formatDataForCell(report, facet)}</td>
                                  );
                              })}
                              <td className={styles['quota-cell-hack']} />
                              <td className={styles.empty} />
                          </tr>
                      );
                  })
                : undefined}
        </tbody>
    );
};
